import type { Sim } from '@/types'

import { palette } from '@/constants/palette'

const COUNT = 2200
const TILT = 0.42

// Samples the hydrogen 3d(z^2) orbital: |psi|^2 ~ r^4 e^(-2r/3) (3cos^2(theta) - 1)^2.
// With the r^2 volume factor the radius follows a Gamma(7, 1.5) distribution,
// and the polar angle is drawn by rejection from the (3c^2 - 1)^2 shape.
const sampleOrbital = () => {
    const xs = new Float32Array(COUNT)
    const ys = new Float32Array(COUNT)
    const zs = new Float32Array(COUNT)
    const points: { x: number, y: number, z: number, sign: number }[] = []
    let positiveCount = 0

    for (let index = 0; index < COUNT; index++) {
        let product = 1

        for (let k = 0; k < 7; k++) product *= 1 - Math.random()

        const radius = -1.5 * Math.log(product)
        let cosine = 0

        do {
            cosine = Math.random() * 2 - 1
        } while (Math.random() * 4 > (3 * cosine * cosine - 1) ** 2)

        const sine = Math.sqrt(1 - cosine * cosine)
        const azimuth = Math.random() * Math.PI * 2

        points.push({
            x: radius * sine * Math.cos(azimuth),
            y: radius * sine * Math.sin(azimuth),
            z: radius * cosine,
            sign: 3 * cosine * cosine - 1 >= 0 ? 1 : -1
        })
    }

    points.sort((a, b) => b.sign - a.sign)

    points.forEach((point, index) => {
        xs[index] = point.x
        ys[index] = point.y
        zs[index] = point.z

        if (point.sign > 0) positiveCount++
    })

    return { xs, ys, zs, positiveCount }
}

export const createOrbital = (): Sim => {
    const { xs, ys, zs, positiveCount } = sampleOrbital()
    let angle = 0.6

    return {
        resize: () => {},

        update: (dt: number) => {
            angle += dt * 0.32
        },

        draw: (ctx: CanvasRenderingContext2D, width: number, height: number) => {
            const scale = (Math.min(width, height) * 0.5) / 17
            const centerX = width / 2
            const centerY = height / 2
            const cosA = Math.cos(angle)
            const sinA = Math.sin(angle)
            const cosT = Math.cos(TILT)
            const sinT = Math.sin(TILT)

            ctx.save()

            ctx.setLineDash([2, 5])
            ctx.strokeStyle = palette.faint
            ctx.lineWidth = 1
            ctx.beginPath()
            ctx.moveTo(centerX, centerY - 17 * scale)
            ctx.lineTo(centerX, centerY + 17 * scale)
            ctx.stroke()
            ctx.setLineDash([])

            ctx.fillStyle = palette.brand

            for (let index = 0; index < COUNT; index++) {
                if (index === positiveCount) ctx.fillStyle = palette.signal

                const rotatedX = xs[index] * cosA - ys[index] * sinA
                const rotatedY = xs[index] * sinA + ys[index] * cosA
                const screenY = zs[index] * cosT - rotatedY * sinT
                const depth = zs[index] * sinT + rotatedY * cosT
                const near = Math.min(1, Math.max(0, (depth + 14) / 28))
                const size = 1 + near * 1.4

                ctx.globalAlpha = 0.16 + near * 0.62
                ctx.fillRect(centerX + rotatedX * scale - size / 2, centerY - screenY * scale - size / 2, size, size)
            }

            ctx.globalAlpha = 1
            ctx.fillStyle = palette.ink
            ctx.beginPath()
            ctx.arc(centerX, centerY, 2.6, 0, Math.PI * 2)
            ctx.fill()

            ctx.font = '500 10px ui-monospace, SFMono-Regular, Menlo, monospace'
            ctx.fillStyle = palette.muted
            ctx.textAlign = 'left'
            ctx.fillText('z', centerX + 6, centerY - 17 * scale + 10)

            ctx.restore()
        }
    }
}
